import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, UserRole } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { useTenantConfig } from '@/contexts/TenantConfigContext';
import { isBeautySnsLabConfig, isBeautySnsLabHost } from '@/config/beautySnsLab';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { config } = useTenantConfig();
  const isBeautyTenant =
    isBeautySnsLabConfig(config) ||
    (typeof window !== "undefined" && isBeautySnsLabHost(window.location.hostname));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    const redirectPath = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/auth?redirect=${encodeURIComponent(redirectPath)}`}
        replace
      />
    );
  }

  if (allowedRoles) {
    const role = user.role;

    if (!role) {
      return <Navigate to="/auth?missingRole=1" replace />;
    }

    if (!allowedRoles.includes(role)) {
      const fallback = role === 'student' ? '/student/dashboard' : '/unauthorized';
      return <Navigate to={fallback} replace />;
    }
  }

  return <>{children}</>;
}
